import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DismissCircleRegular,
  SendClockRegular,
} from '@fluentui/react-icons';
import { getOutstandingItems, updateTaxReturn } from '../services/api';
import type { OutstandingItems, OutstandingReturn, TaxReturn } from '../types';
import { useNotificationToast } from '../hooks/useNotificationToast';

interface EFilePendingSectionProps {
  taxYear: number;
  preparerId?: string;
  enabled?: boolean;
}

/**
 * Dashboard section listing returns that have been e-filed (sent to the taxing authority)
 * and are awaiting a response. Sibling of the other dashboard outstanding-item sections.
 *
 * Quick actions reuse the same "mark e-file status" path staff use on the detail page
 * (PATCH /tax-returns/{id} via updateTaxReturn), so no workflow validation is bypassed:
 *  - Accepted → records acceptance; the backend auto-completes the return to Completed once
 *    payment is satisfied (existing auto-complete rule), otherwise it stays parked at e_filed.
 *  - Rejected → records rejection + reason; the backend keeps the return in the E-File
 *    Rejections rework queue and fires the existing rejection notifications.
 * Either action removes the row from this "awaiting response" list.
 */
export function EFilePendingSection({ taxYear, preparerId, enabled = true }: EFilePendingSectionProps) {
  const queryClient = useQueryClient();
  const { showToast } = useNotificationToast();
  const [rejectTarget, setRejectTarget] = useState<OutstandingReturn | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const queryKey = ['outstandingItems', taxYear, preparerId];

  const { data, isLoading } = useQuery<OutstandingItems>({
    queryKey,
    queryFn: () => getOutstandingItems(taxYear, preparerId),
    enabled,
  });

  const rows = data?.pendingEFileAcceptance ?? [];

  const removeRowFromCache = (id: string) => {
    queryClient.setQueryData<OutstandingItems>(queryKey, (prev) =>
      prev
        ? { ...prev, pendingEFileAcceptance: prev.pendingEFileAcceptance.filter((r) => r.id !== id) }
        : prev
    );
  };

  const invalidateDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['outstandingItems'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
  };

  const acceptMutation = useMutation({
    mutationFn: (row: OutstandingReturn) =>
      updateTaxReturn(row.id, {
        efileStatus: 'accepted',
        efileAcceptedDate: new Date().toISOString(),
      } as Partial<TaxReturn>),
    // Optimistically drop the row so the list feels immediate.
    onMutate: async (row) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<OutstandingItems>(queryKey);
      removeRowFromCache(row.id);
      return { previous };
    },
    onError: (_err, row, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      showToast('Update Failed', `Could not mark ${row.clientName} as accepted.`, 'error');
    },
    onSuccess: (_data, row) => {
      showToast('E-File Accepted', `${row.clientName} marked accepted.`, 'success');
    },
    onSettled: () => invalidateDashboard(),
  });

  const rejectMutation = useMutation({
    mutationFn: (params: { row: OutstandingReturn; reason: string }) =>
      updateTaxReturn(params.row.id, {
        efileStatus: 'rejected',
        efileRejectionReason: params.reason || undefined,
      } as Partial<TaxReturn>),
    onMutate: async ({ row }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<OutstandingItems>(queryKey);
      removeRowFromCache(row.id);
      return { previous };
    },
    onError: (_err, { row }, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      showToast('Update Failed', `Could not mark ${row.clientName} as rejected.`, 'error');
    },
    onSuccess: (_data, { row }) => {
      showToast('E-File Rejected', `${row.clientName} moved to the rejections queue.`, 'warning');
    },
    onSettled: () => invalidateDashboard(),
  });

  const closeRejectDialog = () => {
    setRejectTarget(null);
    setRejectReason('');
  };

  const confirmReject = () => {
    if (!rejectTarget) return;
    const row = rejectTarget;
    const reason = rejectReason;
    closeRejectDialog();
    rejectMutation.mutate({ row, reason });
  };

  const busy = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SendClockRegular />
          Awaiting E-File Response
          {rows.length > 0 && (
            <Badge appearance="filled" color="brand">
              {rows.length}
            </Badge>
          )}
        </span>
      </div>
      <div>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <Spinner size="tiny" label="Loading…" />
          </div>
        ) : rows.length === 0 ? (
          <p className="empty-state">No returns awaiting an e-file response.</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="detail-row" style={{ alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span className="detail-value">{row.clientName}</span>
                <span className="detail-label">
                  {row.details}
                  {row.preparerName ? ` · ${row.preparerName}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  size="small"
                  appearance="primary"
                  icon={<CheckmarkCircleRegular />}
                  disabled={busy}
                  onClick={() => acceptMutation.mutate(row)}
                >
                  Accepted
                </Button>
                <Button
                  size="small"
                  appearance="secondary"
                  icon={<DismissCircleRegular />}
                  disabled={busy}
                  onClick={() => {
                    setRejectReason('');
                    setRejectTarget(row);
                  }}
                >
                  Rejected
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={rejectTarget !== null} onOpenChange={(_, d) => { if (!d.open) closeRejectDialog(); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Reject E-File</DialogTitle>
            <DialogContent>
              <p style={{ marginTop: 0 }}>
                Mark <strong>{rejectTarget?.clientName}</strong>&apos;s e-file as rejected. The return
                stays in the rejections queue for rework.
              </p>
              <p style={{ marginTop: 0, color: tokens.colorPaletteRedForeground1 }}>
                This immediately emails the client and the preparer that the e-file was rejected.
              </p>
              <Field label="Rejection Reason (optional)">
                <Textarea
                  value={rejectReason}
                  onChange={(_, d) => setRejectReason(d.value)}
                  placeholder="Enter the reason for rejection…"
                  rows={3}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeRejectDialog}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmReject}>
                Mark Rejected
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
